/* eslint-disable @typescript-eslint/no-deprecated */
import { z } from 'zod/v4';
import { stringToBooleanSchema } from '../common/index';

// ==================== Session Schemas ====================

export const createSessionSchema = z.object({
  visitorId: z.string().min(1, 'visitorId обязателен'),
  userId: z.number().optional(),
  ip: z.string().optional(),
  userAgent: z.string().optional(),
  device: z.string().optional(),
  browser: z.string().optional(),
  os: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
});

export type CreateSessionDto = z.infer<typeof createSessionSchema>;

export const updateSessionSchema = z.object({
  endedAt: z.string().datetime().optional(),
  userId: z.number().optional(),
});

export type UpdateSessionDto = z.infer<typeof updateSessionSchema>;

// ==================== Action Schemas ====================

export const actionTypeSchema = z.enum([
  // Legacy
  'page_view', 'click', 'scroll', 'focus', 'blur', 'form_submit', 'custom',
  // Navigation
  'page_exit', 'scroll_depth', 'tab_visible', 'tab_hidden',
  // Course
  'course_view', 'course_view_long', 'course_curriculum_expanded', 'course_pricing_viewed',
  'lesson_started', 'lesson_completed', 'lesson_video_play', 'lesson_video_progress',
  'homework_started', 'homework_submitted', 'course_completed', 'course_review_written', 'course_shared',
  // Content
  'article_view', 'article_read_complete', 'announcement_view',
  'post_view', 'post_like', 'post_comment', 'post_share',
  // Search
  'search_query', 'search_result_click', 'search_no_results', 'search_filter_applied',
  // Commerce
  'checkout_started', 'checkout_step', 'checkout_completed', 'checkout_abandoned',
  'subscription_started', 'subscription_cancelled', 'subscription_renewed',
  // Social
  'profile_view', 'teacher_view', 'user_follow', 'user_unfollow', 'chat_message_sent', 'chat_room_entered',
  // Engagement
  'notification_received', 'notification_clicked', 'notification_dismissed',
  'daily_planner_opened', 'goal_created', 'game_started', 'game_completed',
  'achievement_unlocked', 'bonus_earned',
  // Session
  'session_start', 'session_end', 'app_foreground',
]);

export type ActionType = z.infer<typeof actionTypeSchema>;

export const createActionSchema = z.object({
  sessionId: z.number(),
  userId: z.number().optional(),
  type: actionTypeSchema,
  page: z.string().optional(),
  component: z.string().optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
});

export type CreateActionDto = z.infer<typeof createActionSchema>;

// ==================== Query Schemas ====================

export const sessionQuerySchema = z.object({
  page: z.coerce.number().min(1, 'Страница должна быть >= 1').default(1),
  limit: z.coerce.number().min(1, 'Лимит должен быть >= 1').max(100, 'Лимит должен быть <= 100').default(20),
  userId: z.coerce.number().optional(),
  visitorId: z.string().optional(),
  fromDate: z.string().datetime().optional(),
  toDate: z.string().datetime().optional(),
  isActive: stringToBooleanSchema,
});

export type SessionQueryDto = z.infer<typeof sessionQuerySchema>;

export const actionQuerySchema = z.object({
  page: z.coerce.number().min(1, 'Страница должна быть >= 1').default(1),
  limit: z.coerce.number().min(1, 'Лимит должен быть >= 1').max(100, 'Лимит должен быть <= 100').default(50),
  sessionId: z.coerce.number().optional(),
  userId: z.coerce.number().optional(),
  type: actionTypeSchema.optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export type ActionQueryDto = z.infer<typeof actionQuerySchema>;

// ==================== Analytics Schemas ====================

export const userAnalyticsQuerySchema = z.object({
  userId: z.coerce.number(),
  fromDate: z.string().datetime().optional(),
  toDate: z.string().datetime().optional(),
});

export type UserAnalyticsQueryDto = z.infer<typeof userAnalyticsQuerySchema>;

// ==================== Response Types ====================

export interface UserAnalytics {
  totalSessions: number;
  totalActions: number;
  totalTimeSpent: number; // in seconds
  averageSessionDuration: number;
  pageViews: number;
  uniquePages: number;
  lastSeenAt: Date | null;
  isOnline: boolean;
  deviceBreakdown: Record<string, number>;
  browserBreakdown: Record<string, number>;
  topPages: { page: string; views: number }[];
}
